"use client";

import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, MessageCircle, Pencil } from "lucide-react";
import Image from "next/image";
import { AdminEditableText } from "@/components/admin/admin-editable-text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DEFAULT_LANDING_COPY, type LandingCopy } from "@/lib/landing-copy";

interface LandingPageProps {
  onGetStarted: () => void;
}

function HeadlineText({ copy }: { copy: LandingCopy }) {
  const accent = copy.headlineAccent.trim();
  const index = accent ? copy.headline.indexOf(accent) : -1;
  if (index < 0) return <>{copy.headline}</>;

  return (
    <>
      {copy.headline.slice(0, index)}
      <span className="relative inline-block">
        <span className="relative z-10">{accent}</span>
        <motion.span
          className="absolute -bottom-2 left-0 right-0 h-4 bg-gradient-to-r from-[var(--mindup-pink-light)] to-[var(--mindup-pink)]/30 -skew-y-1 rounded"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        />
      </span>
      {copy.headline.slice(index + accent.length)}
    </>
  );
}

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [copy, setCopy] = useState<LandingCopy>(DEFAULT_LANDING_COPY);
  const [isHeadlineOpen, setIsHeadlineOpen] = useState(false);
  const [headlineDraft, setHeadlineDraft] = useState(DEFAULT_LANDING_COPY.headline);
  const [accentDraft, setAccentDraft] = useState(DEFAULT_LANDING_COPY.headlineAccent);
  const [isSavingHeadline, setIsSavingHeadline] = useState(false);
  const [headlineStatus, setHeadlineStatus] = useState<string | null>(null);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const loadCopy = async () => {
      try {
        const response = await fetch("/api/landing-copy", { cache: "no-store" });
        const payload = (await response.json().catch(() => null)) as LandingCopy | null;
        if (response.ok && payload) setCopy({ ...DEFAULT_LANDING_COPY, ...payload });
      } catch {
        // Keep defaults if the API is unavailable.
      }
    };

    const loadAdminSession = async () => {
      try {
        const response = await fetch("/api/admin/session", { cache: "no-store" });
        const payload = (await response.json().catch(() => ({}))) as {
          isAdmin?: boolean;
        };
        setIsAdmin(Boolean(response.ok && payload.isAdmin));
      } catch {
        setIsAdmin(false);
      }
    };

    void loadCopy();
    void loadAdminSession();
  }, []);

  const saveFields = async (patch: Partial<LandingCopy>): Promise<string | null> => {
    try {
      const response = await fetch("/api/landing-copy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const payload = (await response.json().catch(() => ({}))) as
        | LandingCopy
        | { error?: string };
      if (!response.ok || !("headline" in payload)) {
        return ("error" in payload && payload.error) || "Failed to save landing text.";
      }
      setCopy(payload);
      return null;
    } catch {
      return "Failed to save landing text.";
    }
  };

  const openHeadlineEditor = () => {
    setHeadlineDraft(copy.headline);
    setAccentDraft(copy.headlineAccent);
    setHeadlineStatus(null);
    setIsHeadlineOpen(true);
  };

  const saveHeadline = async () => {
    setIsSavingHeadline(true);
    setHeadlineStatus(null);
    const error = await saveFields({
      headline: headlineDraft,
      headlineAccent: accentDraft,
    });
    if (error) {
      setHeadlineStatus(error);
      setIsSavingHeadline(false);
      return;
    }
    setIsHeadlineOpen(false);
    setIsSavingHeadline(false);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[var(--mindup-off-white)]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[var(--mindup-pink-light)]/40 to-[var(--mindup-pink)]/10 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[var(--mindup-mint)]/40 to-[var(--mindup-green)]/10 blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-[var(--mindup-blue-light)]/30 to-[var(--mindup-blue)]/10 blur-3xl" />
      </div>

      <div className="relative h-1 bg-gradient-to-r from-[var(--mindup-pink)] via-[var(--mindup-pink-light)] to-[var(--mindup-pink)]" />

      <header className="relative z-10 container mx-auto px-6 lg:px-8 py-6">
        <motion.div
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div>
            <button
              type="button"
              onClick={() => scrollTo("hero")}
              className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mindup-pink)] rounded"
            >
              <Image
                src="/brand/logos/MU_PrimaryLogo_RGB.png"
                alt="MindUP"
                width={180}
                height={48}
                className="h-10 w-auto"
                priority
              />
            </button>
            <AdminEditableText
              isAdmin={isAdmin}
              title="Edit foundation tagline"
              value={copy.tagline}
              multiline={false}
              onSave={(next) => saveFields({ tagline: next })}
            >
              <p className="text-xs text-[#888] mt-1 tracking-wider uppercase font-bold pr-8">
                {copy.tagline}
              </p>
            </AdminEditableText>
          </div>
          {isAdmin && (
            <Button asChild variant="ghost" className="text-[#555]">
              <Link href="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to app
              </Link>
            </Button>
          )}
        </motion.div>
      </header>

      <section
        id="hero"
        className="relative z-10 container mx-auto px-6 lg:px-8 pt-12 pb-20 lg:pt-20 lg:pb-28 scroll-mt-24"
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-8 right-[8%]"
            animate={{ y: [0, -12, 0], rotate: [0, 8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="w-11 h-11 rounded-2xl bg-[var(--mindup-orange-light)] p-2 shadow-lg shadow-[var(--mindup-orange)]/20 opacity-70">
              <Image
                src="/brand/icons/smiley.gif"
                alt=""
                width={28}
                height={28}
                className="w-full h-full"
                unoptimized
              />
            </div>
          </motion.div>
          <motion.div
            className="absolute bottom-[20%] right-[5%]"
            animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          >
            <div className="w-9 h-9 rounded-xl bg-[var(--mindup-pink-light)] p-1.5 shadow-lg shadow-[var(--mindup-pink)]/20 opacity-60">
              <Image
                src="/brand/icons/squiggle.gif"
                alt=""
                width={24}
                height={24}
                className="w-full h-full"
                unoptimized
              />
            </div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 items-center">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="relative">
              <h1 className="font-display text-5xl lg:text-[3.5rem] xl:text-6xl leading-[0.95] text-[#3E6FDA] pr-10">
                <HeadlineText copy={copy} />
              </h1>
              {isAdmin && (
                <button
                  type="button"
                  onClick={openHeadlineEditor}
                  className="absolute -right-1 -top-1 z-20 h-8 w-8 rounded-md inline-flex items-center justify-center text-[#555]/70 hover:text-[#555] hover:bg-white/80"
                  aria-label="Edit headline"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>

            <AdminEditableText
              isAdmin={isAdmin}
              title="Edit intro"
              value={copy.intro}
              onSave={(next) => saveFields({ intro: next })}
            >
              <p className="text-lg lg:text-xl text-[#555] leading-relaxed pr-8">
                {copy.intro}
              </p>
            </AdminEditableText>

            <AdminEditableText
              isAdmin={isAdmin}
              title="Edit assistant intro"
              value={copy.assistantIntro}
              onSave={(next) => saveFields({ assistantIntro: next })}
            >
              <p className="pr-8">{copy.assistantIntro}</p>
            </AdminEditableText>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <AdminEditableText
                  isAdmin={isAdmin}
                  title="Edit get started button"
                  value={copy.cta}
                  multiline={false}
                  onSave={(next) => saveFields({ cta: next })}
                >
                  <Button
                    size="lg"
                    onClick={onGetStarted}
                    onMouseEnter={() => setIsButtonHovered(true)}
                    onMouseLeave={() => setIsButtonHovered(false)}
                    className="group relative bg-[#7DDBBF] hover:bg-[#6BC9A8] text-[var(--mindup-soft-black)] rounded-full px-12 py-8 text-lg font-bold shadow-lg shadow-[#7DDBBF]/25 hover:shadow-xl hover:shadow-[#7DDBBF]/35 transition-all duration-300"
                  >
                    <MessageCircle className="mr-2 size-6" />
                    {copy.cta}
                    <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </AdminEditableText>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="relative flex justify-center"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <motion.div
              className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[var(--mindup-pink-light)]/60 blur-2xl"
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-6 -left-6 w-40 h-40 rounded-full bg-[var(--mindup-mint)]/50 blur-2xl"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 7, repeat: Infinity, delay: 1 }}
            />
            <motion.div
              className="relative w-full"
              animate={{
                scale: isButtonHovered ? 1.08 : 1,
                filter: isButtonHovered
                  ? "brightness(1.15) drop-shadow(0 0 40px rgba(255, 170, 108, 0.5))"
                  : "brightness(1) drop-shadow(0 0 0 transparent)",
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <Image
                src="/BRAIN_recolored.png"
                alt="MindUP brain — emotional center of learning"
                width={1000}
                height={1000}
                className="w-full h-auto object-contain"
                priority
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="relative z-10 bg-[#FFAA6C] text-white py-16 lg:py-20">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex flex-col items-center gap-10">
            <div className="w-full h-px bg-white/10" />
            <AdminEditableText
              isAdmin={isAdmin}
              title="Edit footer"
              value={copy.footer}
              onSave={(next) => saveFields({ footer: next })}
            >
              <p className="text-xs text-white-900 pr-8">{copy.footer}</p>
            </AdminEditableText>
          </div>
        </div>
      </footer>

      <Dialog open={isHeadlineOpen} onOpenChange={setIsHeadlineOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit headline</DialogTitle>
            <DialogDescription>
              Accent text is underlined if it appears in the headline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Headline</p>
              <Textarea
                value={headlineDraft}
                onChange={(event) => setHeadlineDraft(event.target.value)}
                className="min-h-24"
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Accent word</p>
              <Input
                value={accentDraft}
                onChange={(event) => setAccentDraft(event.target.value)}
              />
            </div>
          </div>
          {headlineStatus && <p className="text-xs text-red-600">{headlineStatus}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsHeadlineOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveHeadline} disabled={isSavingHeadline}>
              {isSavingHeadline ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
